import {
  FetchMFAPreferenceOutput,
  setUpTOTP,
  updateMFAPreference,
} from "aws-amplify/auth";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Totp2FAForm } from "@/features/registration/components/totp-2fa.form";
import { queryClient } from "@/lib/react-query";

type AddTotpModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEnabled: boolean;
  enabled: FetchMFAPreferenceOutput["enabled"];
};

export function AddTotpModal({
  open,
  onOpenChange,
  isEnabled,
  enabled,
}: AddTotpModalProps) {
  const [secret, setSecret] = useState<string | null>(null);
  const [setupUri, setSetupUri] = useState<string | null>(null);

  useEffect(() => {
    const fetchSetup = async () => {
      const totpSetupDetails = await setUpTOTP();
      const appName = "ClassView";
      const setupUri = totpSetupDetails.getSetupUri(appName);
      setupUri.pathname = setupUri.pathname.replace(/%22/g, "");

      setSecret(totpSetupDetails.sharedSecret);
      setSetupUri(setupUri.toString());
    };

    if (open) {
      fetchSetup();
    }
  }, [open]);

  const isLoading = !secret || !setupUri;

  const handleSuccess = async () => {
    const isSmsEnabled = enabled?.includes("SMS");
    const isEmailEnabled = enabled?.includes("EMAIL");
    await updateMFAPreference({
      totp: "ENABLED",
      sms: isSmsEnabled ? "ENABLED" : "DISABLED",
      email: isEmailEnabled ? "ENABLED" : "DISABLED",
    });

    await queryClient.refetchQueries({ queryKey: ["mfa"] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!isEnabled && (
        <DialogTrigger asChild>
          <Button variant="link">+ Add Authenticator App</Button>
        </DialogTrigger>
      )}
      <DialogContent
        aria-describedby={undefined}
        className="rounded-none border-none bg-transparent p-0"
      >
        <DialogTitle className="sr-only">Add Authenticator App</DialogTitle>
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        ) : (
          <Totp2FAForm
            secret={secret}
            setupUri={setupUri}
            goBack={() => onOpenChange(false)}
            onSuccess={handleSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
